
import { useState } from "react";
import { BookingFormData, ParcelDetails } from "@/components/ParcelBookingForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "./useOrganization";
import { useUserProfile } from "./useUserProfile";

const initialFormData: BookingFormData = {
  collectionAddress: {
    street: "",
    street2: "",
    city: "",
    province: "",
    postalCode: "",
    country: "South Africa",
  },
  deliveryAddress: {
    street: "",
    street2: "",
    city: "",
    province: "",
    postalCode: "",
    country: "South Africa",
  },
  collectionContacts: [],
  deliveryContacts: [],
  parcels: [
    {
      type: "documents",
      description: "",
      weight: "",
      dimensions: {
        length: "",
        width: "",
        height: "",
      },
      value: "",
      quantity: 1,
      otherTypeSpecification: "",
      insuranceEnabled: false,
    },
  ],
  collectionDateTime: "",
  instructions: "",
  deliveryPreference: "Cheapest",
};

export const useParcelBookingForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BookingFormData>(initialFormData);
  const [showTerms, setShowTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBookingComplete, setIsBookingComplete] = useState(false);
  const [completedBookingId, setCompletedBookingId] = useState<string | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState<any>(null);
  const [isProfileUpdating, setIsProfileUpdating] = useState(false);
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { profile, profileId } = useUserProfile();

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setCurrentStep(1);
    setShowTerms(false);
    setIsBookingComplete(false);
    setCompletedBookingId(null);
    toast.success("Form has been reset");
  };

  // Check profile completeness BEFORE showing terms
  const handleConfirmBooking = async () => {
    console.log('[BOOKING-FLOW] Confirm booking clicked, checking profile first');
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("You must be logged in to create a booking");
      return;
    }

    // Fetch fresh profile data to ensure accuracy
    const { data: freshProfile, error } = await supabase
      .from('profiles')
      .select('*')
      .or(`id.eq.${user.id},auth_user_id.eq.${user.id}`)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[BOOKING-FLOW] Error fetching profile:', error);
      toast.error("Failed to verify profile. Please try again.");
      return;
    }

    console.log('[BOOKING-FLOW] Fresh profile check:', { 
      found: !!freshProfile, 
      name: freshProfile?.name, 
      email: freshProfile?.email 
    });

    // If profile is incomplete, show profile modal first
    if (!freshProfile?.name || !freshProfile?.email) {
      console.log('[BOOKING-FLOW] Profile incomplete, showing profile completion modal');
      setShowProfileModal(true);
      return;
    }

    // Profile is complete, proceed to show terms dialog
    console.log('[BOOKING-FLOW] Profile complete, showing terms dialog');
    setShowTerms(true);
  };

  const handleSubmit = async () => {
    // This is called AFTER terms are accepted AND profile is confirmed complete
    console.log('[BOOKING-CREATION] Terms accepted, creating booking');
    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to create a booking");
        setIsSubmitting(false);
        return;
      }

      // Profile was already checked, proceed directly to booking creation
      await createBooking(user);

    } catch (error) {
      console.error('[BOOKING-CREATION] Unexpected error:', error);
      toast.error("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };

  const createBooking = async (user: any) => {
    try {
      setIsSubmitting(true);
      
      // Create collection address
      let collectionAddressId = null;
      if (formData.collectionAddress.street) {
        console.log('[BOOKING-CREATION] Creating collection address:', formData.collectionAddress);
        const { data: collectionAddress, error: collectionAddressError } = await supabase
          .from("address_book")
          .insert([
            {
              user_id: user.id,
              street: formData.collectionAddress.street,
              street2: formData.collectionAddress.street2 || null,
              city: formData.collectionAddress.city,
              province: formData.collectionAddress.province,
              postal_code: formData.collectionAddress.postalCode,
              country: formData.collectionAddress.country,
              is_saved: false,
            },
          ])
          .select()
          .single();

        if (collectionAddressError) {
          console.error('[BOOKING-CREATION] Error creating collection address:', collectionAddressError);
          toast.error("Failed to create collection address. Please try again.");
          return;
        }

        collectionAddressId = collectionAddress.id;
        console.log('[BOOKING-CREATION] Collection address created:', collectionAddressId);
      }

      // Create delivery address
      let deliveryAddressId = null;
      if (formData.deliveryAddress.street) {
        console.log('[BOOKING-CREATION] Creating delivery address:', formData.deliveryAddress);
        const { data: deliveryAddress, error: deliveryAddressError } = await supabase
          .from("address_book")
          .insert([
            {
              user_id: user.id,
              street: formData.deliveryAddress.street,
              street2: formData.deliveryAddress.street2 || null,
              city: formData.deliveryAddress.city,
              province: formData.deliveryAddress.province,
              postal_code: formData.deliveryAddress.postalCode,
              country: formData.deliveryAddress.country,
              is_saved: false,
            },
          ])
          .select()
          .single();

        if (deliveryAddressError) {
          console.error('[BOOKING-CREATION] Error creating delivery address:', deliveryAddressError);
          toast.error("Failed to create delivery address. Please try again.");
          return;
        }

        deliveryAddressId = deliveryAddress.id;
        console.log('[BOOKING-CREATION] Delivery address created:', deliveryAddressId);
      }

      // Create booking with address references
      console.log('[BOOKING-CREATION] Creating booking with addresses:', { collectionAddressId, deliveryAddressId });
      const { data: bookingData, error: bookingError } = await supabase
        .from("bookings")
        .insert([
          {
            user_id: user.id,
            collection_datetime: formData.collectionDateTime,
            instructions: formData.instructions,
            delivery_preference: formData.deliveryPreference,
            total_parcels_count: formData.parcels.length,
            collection_address_id: collectionAddressId,
            delivery_address_id: deliveryAddressId,
            org_id: organization?.id || null,
          },
        ])
        .select()
        .single();

      if (bookingError) {
        console.error('[BOOKING-CREATION] Error creating booking:', bookingError);
        toast.error("Failed to create booking. Please try again.");
        return;
      }

      console.log('[BOOKING-CREATION] Booking created:', bookingData.id);

      // Create contacts and link them to the booking
      const contactsToCreate = [];
      const bookingContactsToCreate = [];

      // Process collection contacts
      formData.collectionContacts.forEach((contact, index) => {
        console.log('[BOOKING-CREATION] Processing collection contact:', contact);
        const contactId = crypto.randomUUID();
        contactsToCreate.push({
          id: contactId,
          user_id: user.id,
          name: contact.name,
          phone: contact.phone,
          email: contact.email || null,
          is_saved: false,
        });
        
        bookingContactsToCreate.push({
          booking_id: bookingData.id,
          contact_id: contactId,
          contact_type: 'collection',
          is_primary: index === 0,
          sort_order: index,
        });
      });

      // Process delivery contacts
      formData.deliveryContacts.forEach((contact, index) => {
        console.log('[BOOKING-CREATION] Processing delivery contact:', contact);
        const contactId = crypto.randomUUID();
        contactsToCreate.push({
          id: contactId,
          user_id: user.id,
          name: contact.name,
          phone: contact.phone,
          email: contact.email || null,
          is_saved: false,
        });
        
        bookingContactsToCreate.push({
          booking_id: bookingData.id,
          contact_id: contactId,
          contact_type: 'delivery',
          is_primary: index === 0,
          sort_order: index,
        });
      });

      // Create contacts in contact_book
      if (contactsToCreate.length > 0) {
        console.log('[BOOKING-CREATION] Creating contacts:', contactsToCreate.length);
        const { error: contactsError } = await supabase
          .from("contact_book")
          .insert(contactsToCreate);

        if (contactsError) {
          console.error('[BOOKING-CREATION] Error creating contacts:', contactsError);
          toast.error("Failed to create contacts. Please try again.");
          // Clean up booking if contacts creation fails
          await supabase.from("bookings").delete().eq("id", bookingData.id);
          return;
        }

        // Link contacts to booking
        console.log('[BOOKING-CREATION] Linking contacts to booking:', bookingContactsToCreate.length);
        const { error: bookingContactsError } = await supabase
          .from("booking_contacts")
          .insert(bookingContactsToCreate);

        if (bookingContactsError) {
          console.error('[BOOKING-CREATION] Error linking contacts to booking:', bookingContactsError);
          toast.error("Failed to link contacts to booking. Please try again.");
          // Clean up booking and contacts if linking fails
          await supabase.from("bookings").delete().eq("id", bookingData.id);
          await supabase.from("contact_book").delete().in("id", contactsToCreate.map(c => c.id));
          return;
        }
      }

      // Create parcels for the booking
      const parcelsToInsert = formData.parcels.map((parcel) => ({
        booking_id: bookingData.id,
        parcel_type: parcel.type,
        description: parcel.description,
        weight: parcel.weight,
        dimensions: parcel.dimensions,
        value: parcel.value,
        quantity: parcel.quantity,
        other_type_specification: parcel.otherTypeSpecification,
        insurance_enabled: parcel.insuranceEnabled,
      }));

      console.log('[BOOKING-CREATION] Creating parcels:', parcelsToInsert.length);
      const { error: parcelsError } = await supabase
        .from("booking_parcels")
        .insert(parcelsToInsert);

      if (parcelsError) {
        console.error('[BOOKING-CREATION] Error creating parcels:', parcelsError);
        toast.error("Failed to create parcels. Please try again.");

        // Clean up all created data if parcels creation fails
        await supabase.from("bookings").delete().eq("id", bookingData.id);
        if (contactsToCreate.length > 0) {
          await supabase.from("contact_book").delete().in("id", contactsToCreate.map(c => c.id));
        }
        if (collectionAddressId) {
          await supabase.from("address_book").delete().eq("id", collectionAddressId);
        }
        if (deliveryAddressId) {
          await supabase.from("address_book").delete().eq("id", deliveryAddressId);
        }
        return;
      }

      console.log('[BOOKING-CREATION] Booking creation completed successfully:', bookingData.id);
      toast.success("Booking created successfully!");
      setCompletedBookingId(bookingData.id);
      
      // Reset form data immediately after successful submission
      setFormData(initialFormData);
      setIsBookingComplete(true);
    } catch (error) {
      console.error('[BOOKING-CREATION] Unexpected error during booking creation:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileComplete = async (name: string, email: string) => {
    setIsProfileUpdating(true);
    try {
      console.log('[PROFILE-UPDATE] Updating profile with name and email');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("User not authenticated. Please log in again.");
        return;
      }

      console.log('[PROFILE-UPDATE] User authenticated:', user.id);

      // CRITICAL: Check for profile using BOTH old and new schema
      // Old schema: profiles.id = auth.users.id (auth_user_id is NULL)
      // New schema: profiles.auth_user_id = auth.users.id
      const { data: profiles, error: fetchError } = await supabase
        .from("profiles")
        .select("id, auth_user_id, phone_number")
        .or(`id.eq.${user.id},auth_user_id.eq.${user.id}`)
        .order('created_at', { ascending: true }); // Get oldest first

      if (fetchError) {
        console.error('[PROFILE-UPDATE] Error fetching profile:', fetchError);
        toast.error("Failed to check profile. Please try again.");
        return;
      }

      console.log('[PROFILE-UPDATE] Found profiles:', profiles);

      // Use the first profile (oldest) as the primary one
      const existingProfile = profiles && profiles.length > 0 ? profiles[0] : null;

      let profileOperation;
      
      if (existingProfile) {
        // Profile exists, update it with name and email
        console.log('[PROFILE-UPDATE] Updating existing profile:', existingProfile.id);
        
        // If this is an old-style profile (no auth_user_id), update it to new schema
        const updateData: any = { name, email };
        if (!existingProfile.auth_user_id) {
          console.log('[PROFILE-UPDATE] Migrating old profile to new schema');
          updateData.auth_user_id = user.id;
        }
        
        profileOperation = await supabase
          .from("profiles")
          .update(updateData)
          .eq("id", existingProfile.id);
          
        // If there are duplicate profiles (old + new), delete the newer ones
        if (profiles && profiles.length > 1) {
          console.log('[PROFILE-UPDATE] Deleting duplicate profiles');
          const duplicateIds = profiles.slice(1).map(p => p.id);
          await supabase
            .from("profiles")
            .delete()
            .in("id", duplicateIds);
        }
      } else {
        // Profile doesn't exist, create it
        console.log('[PROFILE-UPDATE] Creating new profile for user:', user.id);
        profileOperation = await supabase
          .from("profiles")
          .insert([{
            id: user.id, // Use user.id as primary key for consistency with bookings
            auth_user_id: user.id,
            name,
            email,
            phone_number: user.phone || null,
          }]);
      }

      if (profileOperation.error) {
        console.error('[PROFILE-UPDATE] Error updating profile:', profileOperation.error);
        toast.error("Failed to update profile. Please try again.");
        return;
      }

      console.log('[PROFILE-UPDATE] Profile updated successfully');
      toast.success("Profile updated successfully!");
      
      // Close modal and proceed to terms dialog
      setShowProfileModal(false);
      
      // Now show terms dialog since profile is complete
      console.log('[PROFILE-UPDATE] Profile complete, showing terms dialog');
      setShowTerms(true);
    } catch (error) {
      console.error('[PROFILE-UPDATE] Unexpected error:', error);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setIsProfileUpdating(false);
    }
  };

  const navigateToBookings = () => {
    resetForm();
    if (completedBookingId) {
      navigate(`/bookings#${completedBookingId}`);
    } else {
      navigate('/bookings');
    }
  };

  return {
    currentStep,
    formData,
    setFormData,
    handleNext,
    handlePrevious,
    handleSubmit,
    handleConfirmBooking,
    showTerms,
    setShowTerms,
    isSubmitting,
    isBookingComplete,
    navigateToBookings,
    resetForm,
    showProfileModal,
    handleProfileComplete,
    isProfileUpdating,
  };
};
